import React, { useState, useCallback, useMemo } from 'react'
import { Input, Button, Space, Empty, message } from 'antd'
import styled from 'styled-components'
import {
  IconSearch,
  IconPlus,
  IconLayoutGrid,
  IconList,
  IconRefresh,
  IconChevronDown,
  IconChevronUp
} from '@tabler/icons-react'
import GroupCard from '../GroupCard'
import TableAPI from '../TableAPI'
import { column_usergroup } from '../AdminUserModal/UserInfoTabs/column_usergroup'
import API from '../../../utils/api'
import { useToken } from 'app/hooks'

const Container = styled.div`
  padding: 24px;
  background: #f8f9fa;
  min-height: calc(100vh - 120px);
  position: relative;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
`

const TitleSection = styled.div`
  color: #2c3e50;

  h1 {
    color: #2c3e50;
    margin: 0 0 8px 0;
    font-size: 28px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  p {
    color: #6c757d;
    margin: 0;
    font-size: 14px;
  }
`

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`

const SearchInput = styled(Input)`
  width: 300px;
  background: #ffffff;
  border: 1px solid #e9ecef;
  color: #2c3e50;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    background: #ffffff;
    border-color: #1890ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`

const ActionButton = styled(Button)<{ $variant?: 'primary' | 'secondary' }>`
  ${props => props.$variant === 'primary' ? `
    background: linear-gradient(45deg, #667eea, #764ba2);
    border: none;
    color: white;

    &:hover {
      background: linear-gradient(45deg, #764ba2, #667eea);
      color: white;
    }
  ` : `
    background: #ffffff;
    border: 1px solid #e9ecef;
    color: #6c757d;

    &:hover {
      background: #f8f9fa;
      border-color: #dee2e6;
      color: #495057;
    }
  `}
`

const StatsBar = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  position: relative;
  z-index: 1;
`

const StatItem = styled.div`
  background: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: linear-gradient(45deg, #667eea, #764ba2);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }

  .stat-content {
    .stat-value {
      color: #2c3e50;
      font-size: 24px;
      font-weight: 700;
      margin: 0;
    }

    .stat-label {
      color: #6c757d;
      font-size: 12px;
      margin: 0;
    }
  }
`

const GridContainer = styled.div<{ $viewMode?: 'grid' | 'list' }>`
  position: relative;
  z-index: 1;

  ${props => props.$viewMode === 'grid' ? `
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    grid-auto-rows: min-content;
    gap: 12px;
    align-items: start;

    @media (max-width: 1200px) {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  ` : `
    display: flex;
    flex-direction: column;
    gap: 8px;
  `}
`

const DropIndicator = styled.div<{ $position: 'left' | 'right'; $show: boolean }>`
  position: absolute;
  top: 10px;
  bottom: 10px;
  width: 4px;
  background: linear-gradient(180deg, #1890ff, #40a9ff);
  border-radius: 2px;
  opacity: ${props => props.$show ? 1 : 0};
  transform: ${props => props.$show ? 'scaleY(1)' : 'scaleY(0)'};
  transition: all 0.2s ease;
  z-index: 10;
  box-shadow: 0 0 8px rgba(24, 144, 255, 0.6);

  ${props => props.$position === 'left' ? `
    left: -8px;
  ` : `
    right: -8px;
  `}

  &::before {
    content: '';
    position: absolute;
    left: -2px;
    top: -4px;
    width: 8px;
    height: 8px;
    background: #1890ff;
    border-radius: 50%;
    box-shadow: 0 0 4px rgba(24, 144, 255, 0.8);
  }

  &::after {
    content: '';
    position: absolute;
    left: -2px;
    bottom: -4px;
    width: 8px;
    height: 8px;
    background: #1890ff;
    border-radius: 50%;
    box-shadow: 0 0 4px rgba(24, 144, 255, 0.8);
  }
`

const CardWrapper = styled.div`
  position: relative;
`


const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;

  .ant-empty-description {
    color: #6c757d;
    font-size: 16px;
  }
`

interface GroupCardGridProps {
  groups: any[]
  loading?: boolean
  onDataChange?: () => void
  onAddGroup?: () => void
}

// LegacyTableView 컴포넌트
interface LegacyTableViewProps {
  groups: any[]
  onDataChange?: () => void
}

const LegacyTableView: React.FC<LegacyTableViewProps> = ({ groups, onDataChange }) => {
  const [tableRefresh, setTableRefresh] = useState(0)

  const handleRefresh = useCallback(() => {
    setTableRefresh(prev => prev + 1)
    onDataChange?.()
  }, [onDataChange])

  return (
    <TableAPI
      title="회원그룹목록"
      columns={column_usergroup}
      usermodal={false}
      height={false}
      pagination={true}
      defaultMaxData={50}
      apikey={`/admin/usergroup?t=${tableRefresh}`}
      onDataChange={handleRefresh}
    />
  )
}

const GroupCardGrid: React.FC<GroupCardGridProps> = ({
  groups = [],
  loading = false,
  onDataChange,
  onAddGroup
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list')
  const [draggedGroup, setDraggedGroup] = useState<any>(null)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [dropPosition, setDropPosition] = useState<'left' | 'right' | null>(null)
  const [dragMode, setDragMode] = useState<'reorder' | 'assign-parent'>('reorder')
  const [isUpdating, setIsUpdating] = useState(false)
  const [updatingGroupId, setUpdatingGroupId] = useState<number | null>(null)
  const [allExpanded, setAllExpanded] = useState(true) // 기본값을 true로 설정
  const { data: token } = useToken()

  // 그룹 데이터를 상위/하위 그룹으로 구조화하고 정렬
  const structuredGroups = useMemo(() => {
    console.log('Raw groups data:', groups)

    // 1단계: 하위그룹 식별 (subgroup === 1 또는 parentid가 있음)
    const subgroups = groups.filter(group =>
      group.subgroup === 1 || (group.parentid && group.parentid > 0)
    )

    // 2단계: 실제 부모 그룹들의 ID 수집
    const actualParentIds = new Set(
      subgroups.map(sub => sub.parentid).filter(id => id && id > 0)
    )

    // 3단계: 부모 그룹 식별 - 실제로 자식을 가진 그룹들만
    const parentGroups = groups.filter(group => actualParentIds.has(group.id))

    // 4단계: 독립 그룹 (부모도 자식도 아닌 그룹)
    const standaloneGroups = groups.filter(group =>
      group.subgroup !== 1 &&
      !group.parentid &&
      !actualParentIds.has(group.id)
    )

    console.log('Parent groups:', parentGroups)
    console.log('Subgroups:', subgroups)
    console.log('Standalone groups:', standaloneGroups)

    // 각 그룹의 상세 정보 로깅
    parentGroups.forEach(parent => {
      console.log(`Parent Group ID ${parent.id}: name="${parent.name}", subgroup=${parent.subgroup}`)
    })
    subgroups.forEach(sub => {
      console.log(`Subgroup ID ${sub.id}: name="${sub.name}", subgroup=${sub.subgroup}, parentid=${sub.parentid}`)
    })
    standaloneGroups.forEach(standalone => {
      console.log(`Standalone Group ID ${standalone.id}: name="${standalone.name}", subgroup=${standalone.subgroup}`)
    })

    // 부모 그룹들을 seq 순으로 정렬하고 자식 그룹들과 연결
    const parentWithChildren = parentGroups.map(parent => {
      const childSubgroups = subgroups
        .filter(sub => sub.parentid === parent.id)
        .sort((a, b) => {
          // seq 값을 숫자로 변환 (null, undefined, 빈 문자열은 0으로 처리)
          const aSeq = parseInt(a.seq) || 0
          const bSeq = parseInt(b.seq) || 0

          console.log(`Sorting subgroups: ${a.name}(seq:${a.seq}→${aSeq}) vs ${b.name}(seq:${b.seq}→${bSeq})`)

          // 0인 것들이 먼저 오도록 정렬
          if (aSeq === 0 && bSeq !== 0) return -1
          if (aSeq !== 0 && bSeq === 0) return 1
          if (aSeq === bSeq) return a.id - b.id
          return aSeq - bSeq
        })

      console.log(`Parent ${parent.id} (${parent.name}) has ${childSubgroups.length} subgroups:`, childSubgroups.map(s => `${s.id}(${s.name})`))

      return {
        ...parent,
        subgroups: childSubgroups
      }
    }).sort((a, b) => {
      // seq 값을 숫자로 변환 (null, undefined, 빈 문자열은 0으로 처리)
      const aSeq = parseInt(a.seq) || 0
      const bSeq = parseInt(b.seq) || 0

      console.log(`Sorting parent groups: ${a.name}(seq:${a.seq}→${aSeq}) vs ${b.name}(seq:${b.seq}→${bSeq})`)

      // 0인 것들이 먼저 오도록 정렬
      if (aSeq === 0 && bSeq !== 0) return -1
      if (aSeq !== 0 && bSeq === 0) return 1
      if (aSeq === bSeq) return a.id - b.id
      return aSeq - bSeq
    })

    // 독립 그룹들을 seq 순으로 정렬하고 빈 subgroups 배열 추가
    const standaloneWithEmpty = standaloneGroups.map(group => ({
      ...group,
      subgroups: []
    })).sort((a, b) => {
      // seq 값을 숫자로 변환 (null, undefined, 빈 문자열은 0으로 처리)
      const aSeq = parseInt(a.seq) || 0
      const bSeq = parseInt(b.seq) || 0

      console.log(`Sorting standalone groups: ${a.name}(seq:${a.seq}→${aSeq}) vs ${b.name}(seq:${b.seq}→${bSeq})`)

      // 0인 것들이 먼저 오도록 정렬
      if (aSeq === 0 && bSeq !== 0) return -1
      if (aSeq !== 0 && bSeq === 0) return 1
      if (aSeq === bSeq) return a.id - b.id
      return aSeq - bSeq
    })

    // 최종 결과: 부모+자식 그룹들과 독립 그룹들 합치기 후 다시 seq 순으로 정렬
    const combined = [...parentWithChildren, ...standaloneWithEmpty]

    const result = combined.sort((a, b) => {
      const aSeq = parseInt(a.seq) || 0
      const bSeq = parseInt(b.seq) || 0

      console.log(`Final sorting: ${a.name}(seq:${a.seq}→${aSeq}) vs ${b.name}(seq:${b.seq}→${bSeq})`)

      // 0인 것들이 먼저 오도록 정렬
      if (aSeq === 0 && bSeq !== 0) return -1
      if (aSeq !== 0 && bSeq === 0) return 1
      if (aSeq === bSeq) return a.id - b.id
      return aSeq - bSeq
    })

    console.log('=== FINAL GROUP ORDER ===')
    result.forEach((group, index) => {
      console.log(`${index + 1}. ${group.name} (seq: ${group.seq}) - ${group.subgroups?.length || 0} subgroups`)
    })
    console.log('Final structured groups:', result)
    return result
  }, [groups])

  // 검색 필터링 - 구조화된 그룹에 적용
  const filteredStructuredGroups = useMemo(() => {
    if (!searchTerm) return structuredGroups

    return structuredGroups.filter(parent => {
      const parentMatch = parent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         parent.id?.toString().includes(searchTerm)

      const subgroupMatch = parent.subgroups?.some((sub: any) =>
        sub.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.id?.toString().includes(searchTerm)
      )

      return parentMatch || subgroupMatch
    }).map(parent => ({
      ...parent,
      subgroups: parent.subgroups?.filter((sub: any) =>
        !searchTerm ||
        sub.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.id?.toString().includes(searchTerm) ||
        parent.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parent.id?.toString().includes(searchTerm)
      )
    }))
  }, [structuredGroups, searchTerm])

  // 통계 계산
  const stats = useMemo(() => {
    const totalGroups = groups.length

    // 하위그룹: subgroup === 1 또는 parentid가 있는 그룹
    const subGroups = groups.filter(g =>
      g.subgroup === 1 || (g.parentid && g.parentid > 0)
    ).length

    // 상위그룹: 전체 - 하위그룹
    const parentGroups = totalGroups - subGroups

    const totalUsers = groups.reduce((sum, g) => sum + (g.usercount || 0), 0)

    console.log('Statistics calculation:', {
      totalGroups,
      parentGroups,
      subGroups,
      subgroupDetails: groups.filter(g => g.subgroup === 1 || (g.parentid && g.parentid > 0)).map(g => ({
        id: g.id,
        name: g.name,
        subgroup: g.subgroup,
        parentid: g.parentid
      }))
    })

    return { totalGroups, parentGroups, subGroups, totalUsers }
  }, [groups])

  const handleDragStart = useCallback((e: React.DragEvent, group: any) => {
    const groupIndex = filteredStructuredGroups.findIndex(g => g.id === group.id)
    setDraggedGroup(group)
    setDraggedIndex(groupIndex)

    // Ctrl 키를 누르고 드래그하면 상위그룹 지정 모드
    const isAssignParentMode = e.ctrlKey || e.metaKey
    setDragMode(isAssignParentMode ? 'assign-parent' : 'reorder')

    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', group.id.toString())
  }, [filteredStructuredGroups])

  const handleDragEnd = useCallback(() => {
    setDraggedGroup(null)
    setDraggedIndex(null)
    setHoveredIndex(null)
    setDragOverIndex(null)
    setDropPosition(null)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent, targetGroup: any, element?: HTMLElement) => {
    e.preventDefault()
    const targetIndex = filteredStructuredGroups.findIndex(g => g.id === targetGroup.id)

    if (dragMode === 'assign-parent') {
      // 상위그룹 지정 모드: 기존 효과 사용
      const canBeParent = !targetGroup.subgroups || targetGroup.subgroups.length === 0
      setDragOverIndex(canBeParent ? targetIndex : null)
    } else {
      // 순서 변경 모드: 삽입 위치 결정 (좌우)
      if (element) {
        const rect = element.getBoundingClientRect()
        const mouseX = e.clientX
        const elementCenter = rect.left + rect.width / 2

        setDragOverIndex(targetIndex)
        setDropPosition(mouseX < elementCenter ? 'left' : 'right')
      }
    }
  }, [filteredStructuredGroups, dragMode])

  const handleDrop = useCallback(async (e: React.DragEvent, targetGroup: any) => {
    e.preventDefault()

    if (!draggedGroup || !token || draggedGroup.id === targetGroup.id) {
      setDraggedGroup(null)
      setDraggedIndex(null)
      setHoveredIndex(null)
      setDragOverIndex(null)
      return
    }

    const targetIndex = filteredStructuredGroups.findIndex(g => g.id === targetGroup.id)
    const dragIndex = draggedIndex

    if (dragIndex === null || targetIndex === -1) {
      setDraggedGroup(null)
      setDraggedIndex(null)
      setHoveredIndex(null)
      setDragOverIndex(null)
      setDropPosition(null)
      return
    }

    try {
      setIsUpdating(true)
      setUpdatingGroupId(draggedGroup.id)

      if (dragMode === 'assign-parent') {
        // 상위그룹 지정 모드
        if (targetGroup.subgroups && targetGroup.subgroups.length > 0) {
          message.warning('하위그룹을 가진 그룹은 상위그룹으로 지정할 수 없습니다')
          return
        }

        if (draggedGroup.subgroups && draggedGroup.subgroups.length > 0) {
          message.warning('하위그룹을 가진 그룹은 다른 그룹의 하위로 들어갈 수 없습니다')
          return
        }

        await API.send('PUT', `/admin/usergroup/${draggedGroup.id}`, token + '', {
          name: draggedGroup.name,
          info_d: draggedGroup.info_d,
          info_w: draggedGroup.info_w,
          cs_1: draggedGroup.cs_1,
          cs_2: draggedGroup.cs_2,
          cs_3: draggedGroup.cs_3,
          cs_4: draggedGroup.cs_4,
          kor_1: draggedGroup.kor_1,
          kor_2: draggedGroup.kor_2,
          kor_3: draggedGroup.kor_3,
          seq: draggedGroup.seq,
          parentid: targetGroup.id
        })

        message.success('상위그룹 지정 완료')
        console.log(`Assigned group ${draggedGroup.id} as subgroup of ${targetGroup.id}`)
      } else {
        // 순서 변경 모드: 전체 순서 재배정
        const allGroups = [...filteredStructuredGroups]

        // 새로운 순서 계산 (좌우 기준)
        let newIndex = targetIndex
        if (dropPosition === 'right') {
          newIndex = targetIndex + 1
        }

        // 드래그된 항목을 배열에서 제거
        const [draggedItem] = allGroups.splice(dragIndex, 1)

        // 새 위치에 삽입
        if (newIndex > dragIndex) {
          newIndex-- // 제거로 인한 인덱스 조정
        }
        allGroups.splice(newIndex, 0, draggedItem)

        // 모든 그룹의 seq를 순서대로 재배정
        const updatePromises = allGroups.map(async (group, index) => {
          const newSeq = index + 1
          if (group.seq !== newSeq) {
            await API.send('PUT', `/admin/usergroup/${group.id}`, token + '', {
              name: group.name,
              info_d: group.info_d,
              info_w: group.info_w,
              cs_1: group.cs_1,
              cs_2: group.cs_2,
              cs_3: group.cs_3,
              cs_4: group.cs_4,
              kor_1: group.kor_1,
              kor_2: group.kor_2,
              kor_3: group.kor_3,
              seq: newSeq,
              parentid: group.parentid
            })
          }
        })

        await Promise.all(updatePromises)
        message.success('순서 변경 완료')
        console.log('Reordered all groups with new sequence numbers')
      }

      onDataChange?.()
    } catch (error) {
      message.error('처리 중 오류가 발생했습니다')
      console.error('Failed to update:', error)
    } finally {
      setIsUpdating(false)
      setUpdatingGroupId(null)
      setDraggedGroup(null)
      setDraggedIndex(null)
      setHoveredIndex(null)
      setDragOverIndex(null)
      setDropPosition(null)
    }
  }, [draggedGroup, draggedIndex, filteredStructuredGroups, token, onDataChange, dragMode, dropPosition])

  const handleRefresh = useCallback(() => {
    onDataChange?.()
  }, [onDataChange])

  if (loading) {
    return (
      <Container>
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(255,255,255,0.6)' }}>
          로딩 중...
        </div>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <TitleSection>
          <h1>
            <IconLayoutGrid size={32} />
            회원 그룹 관리
          </h1>
          <p>그룹별 회원 관리 및 설정을 할 수 있습니다</p>
        </TitleSection>

        <Controls>
          <SearchInput
            placeholder="그룹명 또는 ID로 검색..."
            prefix={<IconSearch size={16} style={{ color: 'rgba(255,255,255,0.4)' }} />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <Space>
            <ActionButton
              icon={allExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
              onClick={() => setAllExpanded(!allExpanded)}
            >
              {allExpanded ? '전체 접기' : '전체 펼치기'}
            </ActionButton>

            <ActionButton
              icon={viewMode === 'grid' ? <IconList size={16} /> : <IconLayoutGrid size={16} />}
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
            >
              {viewMode === 'grid' ? '목록' : '그리드'}
            </ActionButton>

            <ActionButton
              icon={<IconRefresh size={16} />}
              onClick={handleRefresh}
            >
              새로고침
            </ActionButton>

            <ActionButton
              $variant="primary"
              icon={<IconPlus size={16} />}
              onClick={onAddGroup}
            >
              그룹 추가
            </ActionButton>
          </Space>
        </Controls>
      </Header>

      <StatsBar>
        <StatItem>
          <div className="stat-icon">
            <IconLayoutGrid size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalGroups}</div>
            <div className="stat-label">전체 그룹</div>
          </div>
        </StatItem>

        <StatItem>
          <div className="stat-icon">
            <IconList size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.parentGroups}</div>
            <div className="stat-label">상위 그룹</div>
          </div>
        </StatItem>

        <StatItem>
          <div className="stat-icon">
            <IconPlus size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.subGroups}</div>
            <div className="stat-label">하위 그룹</div>
          </div>
        </StatItem>

        <StatItem>
          <div className="stat-icon">
            <IconSearch size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalUsers}</div>
            <div className="stat-label">총 회원수</div>
          </div>
        </StatItem>
      </StatsBar>

      {filteredStructuredGroups.length === 0 ? (
        <EmptyState>
          <Empty
            description={
              searchTerm ?
                `"${searchTerm}"에 대한 검색 결과가 없습니다` :
                "아직 생성된 그룹이 없습니다"
            }
          />
          {!searchTerm && (
            <ActionButton
              $variant="primary"
              icon={<IconPlus size={16} />}
              onClick={onAddGroup}
              style={{ marginTop: 16 }}
            >
              첫 번째 그룹 만들기
            </ActionButton>
          )}
        </EmptyState>
      ) : viewMode === 'grid' ? (
        <GridContainer $viewMode={viewMode}>
          {filteredStructuredGroups.map((parentGroup, index) => (
            <CardWrapper key={parentGroup.id}>
              {/* 순서 변경 모드일 때만 삽입 라인 표시 */}
              {dragMode === 'reorder' && (
                <>
                  <DropIndicator
                    $position="left"
                    $show={dragOverIndex === index && dropPosition === 'left' && draggedGroup?.id !== parentGroup.id}
                  />
                  <DropIndicator
                    $position="right"
                    $show={dragOverIndex === index && dropPosition === 'right' && draggedGroup?.id !== parentGroup.id}
                  />
                </>
              )}
              <GroupCard
                group={parentGroup}
                subgroups={parentGroup.subgroups}
                onDragStart={isUpdating ? undefined : handleDragStart}
                onDataChange={onDataChange}
                index={index}
                onDragOver={isUpdating ? undefined : (e) => {
                  const element = e.currentTarget as HTMLElement
                  handleDragOver(e, parentGroup, element)
                }}
                onDrop={isUpdating ? undefined : handleDrop}
                onDragEnd={handleDragEnd}
                isDragging={draggedGroup?.id === parentGroup.id}
                isDragOver={dragMode === 'assign-parent' && dragOverIndex === index && draggedGroup?.id !== parentGroup.id}
                isUpdating={updatingGroupId === parentGroup.id}
                dragMode={dragMode}
                forceExpanded={allExpanded}
              />
            </CardWrapper>
          ))}
        </GridContainer>
      ) : (
        /* 목록 뷰 - 예전 TableAPI 컴포넌트 사용 */
        <div style={{
          background: '#ffffff',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          minHeight: '600px'
        }}>
          <LegacyTableView
            groups={groups}
            onDataChange={onDataChange}
          />
        </div>
      )}
    </Container>
  )
}

export default GroupCardGrid